import { ChatBubbleLeftRightIcon, EnvelopeIcon, GlobeAltIcon, InformationCircleIcon, MapPinIcon, SignalIcon, UserGroupIcon } from '@heroicons/react/24/outline';

import { Badge } from '@/components/core/Badge';
import { Callout } from '@/components/core/Callout';
import { Checkbox } from '@/components/core/Checkbox';
import { Icon } from '@/components/core/Icon';
import { AttributeComponent } from '@/components/datamodel/AttributeComponent';
import { ForceAttributeEditableProvider } from '@/components/datamodel/hooks/useIsAttributeReadonly';
import { LayoutProvider } from '@/components/datamodel/hooks/useLayout';
import { cn } from '@/services/utils';
import type { DataModelAttribute } from '@/types/DataModel';

import { getChannelId } from '../lib/publicationValues';
import type { ChannelDestination, OrganizationDestination } from '../types';

type OrganizationCardProps = {
    organization: OrganizationDestination;
    onToggle: () => void;
};

const OrganizationCard = ({ organization, onToggle }: OrganizationCardProps) => {
    return (
        <div
            id={`organization_${organization.organizationId}`}
            className="tw-border tw-border-divider tw-rounded-sm tw-p-3 tw-flex tw-gap-3 tw-items-center"
            data-testid={`organization-${organization.organizationId}`}
        >
            <Checkbox
                isChecked={Boolean(organization.isSelected)}
                onCheckedChange={onToggle}
                aria-label={organization.name}
            />
            <button
                type="button"
                className="tw-flex tw-gap-3 tw-items-start tw-text-left tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer tw-flex-1"
                onClick={onToggle}
            >
                <div className="tw-w-12 tw-h-12 tw-rounded-full tw-bg-fill-secondary tw-flex tw-items-center tw-justify-center tw-overflow-hidden tw-shrink-0">
                    {organization.imageUrl ? (
                        <img src={organization.imageUrl} alt={organization.name} className="tw-w-full tw-h-full tw-object-cover" />
                    ) : (
                        <Icon icon={UserGroupIcon} size="md" className='tw-icon-primary' />
                    )}
                </div>
                <div className="tw-flex tw-flex-col tw-gap-1 tw-min-w-0">
                    <div className="tw-flex tw-items-center tw-gap-1 tw-truncate">
                        <span className="tw-truncate tw-text-t-base tw-text-t-primary">{organization.name}</span>
                    </div>
                    {organization.address && (
                        <div className="tw-flex tw-items-center tw-gap-1 tw-truncate">
                            <Icon icon={MapPinIcon} size="sm" className='tw-icon-secondary' />
                            <span className="tw-truncate tw-text-t-sm tw-text-t-primary">{organization.address}</span>
                        </div>
                    )}
                    {organization.email && (
                        <div className="tw-flex tw-items-center tw-gap-1 tw-truncate">
                            <Icon icon={EnvelopeIcon} size="sm" className='tw-icon-secondary' />
                            <span className="tw-truncate tw-text-t-sm tw-text-t-secondary">{organization.email}</span>
                        </div>
                    )}
                    {organization.url && (
                        <div className="tw-flex tw-items-center tw-gap-1 tw-truncate">
                            <Icon icon={GlobeAltIcon} size="sm" className='tw-icon-secondary' />
                            <span className="tw-truncate tw-text-t-sm tw-text-t-secondary">{organization.url}</span>
                        </div>
                    )}
                </div>
            </button>
        </div>
    );
};

type ChannelCardProps = {
    channel: ChannelDestination;
    attributes: DataModelAttribute[];
    onToggle: () => void;
};

const ChannelCard = ({ channel, attributes, onToggle }: ChannelCardProps) => {
    const title = channel.subDestinationLabel || channel.subDestinationKey || channel.name;
    const channelId = getChannelId(channel);

    return (
        <div
            id={channel.subDestinationKey
                ? `communicationChannel_${channel.id}_${channel.subDestinationKey}`
                : `communicationChannel_${channel.id}`}
            className={cn(
                'tw-border tw-border-divider tw-rounded-sm tw-p-3 tw-flex tw-flex-col tw-gap-4'
            )}
            data-testid={`channel-${channelId}${channel.subDestinationKey ? `-${channel.subDestinationKey}` : ''}`}
        >
            <div className="tw-flex tw-gap-3 tw-items-center">
                <Checkbox
                    isChecked={Boolean(channel.isSelected)}
                    onCheckedChange={onToggle}
                    aria-label={title}
                />
                <button
                    type="button"
                    className="tw-flex tw-gap-3 tw-items-start tw-text-left tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer tw-flex-1"
                    onClick={onToggle}
                >
                    <div className="tw-w-12 tw-h-12 tw-rounded-full tw-bg-fill-secondary tw-flex tw-items-center tw-justify-center tw-shrink-0">
                        <Icon icon={ChatBubbleLeftRightIcon} size="md" className='tw-icon-primary' />
                    </div>
                    <div className="tw-flex tw-flex-col tw-gap-1 tw-min-w-0">
                        <div className="tw-flex tw-items-center tw-gap-1 tw-truncate">
                            <span className="tw-truncate tw-text-t-base tw-text-t-primary">{title}</span>
                        </div>
                        {channel.subDestinationKey && (
                            <>
                                <div className="tw-flex tw-items-center tw-gap-1 tw-truncate">
                                    <Icon icon={SignalIcon} size="sm" className='tw-icon-primary' />
                                    <span className="tw-truncate tw-text-t-sm tw-text-t-secondary">{channel.name}</span>
                                </div>
                                <div className="tw-flex tw-items-center tw-gap-1 tw-truncate">
                                    <span className="tw-truncate tw-text-t-sm tw-text-t-secondary">{channel.subDestinationKey}</span>
                                </div>
                            </>
                        )}
                        {(channel.planLabel || channel.type) && (
                            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1">
                                {channel.planLabel && (
                                    <Badge variant="info">{channel.planLabel}</Badge>
                                )}
                                {channel.type && (
                                    <Badge variant="info">{channel.type}</Badge>
                                )}
                            </div>
                        )}
                    </div>
                </button>
            </div>
            {attributes.length > 0 && (
                <ForceAttributeEditableProvider value={true}>
                    <LayoutProvider layoutName="edit">
                        <div className="tw-flex tw-flex-col tw-gap-4 tw-pl-7 [&_.clear-button-container]:tw-gap-1">
                            {attributes.map((attribute) => (
                                <div key={attribute.name} className="tw-flex tw-flex-col tw-gap-1">
                                    {attribute.description && (
                                        <Callout variant="info" canBeClosed={false} className="tw-mb-1">
                                            <span className={cn('tw-flex tw-items-start tw-gap-1')}>
                                                <Icon icon={InformationCircleIcon} size="sm" variant="secondary" />
                                                {attribute.description}
                                            </span>
                                        </Callout>
                                    )}
                                    <AttributeComponent
                                        attribute={attribute}
                                        fieldPath={`${channelId}.${attribute.name}`}
                                        disableUrlSync={true}
                                        direction="vertical"
                                    />
                                </div>
                            ))}
                        </div>
                    </LayoutProvider>
                </ForceAttributeEditableProvider>
            )}
        </div>
    );
};

export { ChannelCard, OrganizationCard };
